import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { useCompany } from '../../app/providers/CompanyProvider';
export function HeaderCompanySelector() {
  const {
    selectedCompany,
    companies,
    setSelectedCompany
  } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);
  const selectCompany = (company: typeof selectedCompany) => {
    setSelectedCompany(company);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-13669">
      <button onClick={() => setIsOpen(!isOpen)} aria-label={`Selected company: ${selectedCompany.name}`} aria-expanded={isOpen} aria-haspopup="listbox" className="flex items-center justify-between w-full sm:min-w-48 sm:w-auto max-w-[160px] sm:max-w-64 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-13670">
        <div className="flex items-center min-w-0 flex-1" data-id="element-13671">
          {selectedCompany.logo ? <img src={selectedCompany.logo} alt="" className="w-4 h-4 rounded-full mr-2 flex-shrink-0" data-id="element-13672" /> : <BuildingIcon size={14} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-13673" />}
          <span className="truncate text-xs" data-id="element-13674">{selectedCompany.name}</span>
        </div>
        <ChevronDownIcon size={14} className={`ml-2 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-13675" />
      </button>

      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-13676" />

          <div role="listbox" aria-label="Select company or sub merchant" className="absolute left-0 z-20 w-[280px] sm:w-80 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" data-id="element-13677">
            {/* Master merchant section */}
            <div className="border-b border-gray-200" data-id="element-13678">
              <div className="px-3 py-2 bg-gray-50" data-id="element-13679">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-13680">
                  Master Merchant
                </span>
              </div>
              {companies.filter(c => c.type === 'master').map(company => {
            const isSelected = selectedCompany.id === company.id;
            return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-13681">
                      <div className="flex items-center flex-1 min-w-0" data-id="element-13682">
                        {company.logo ? <img src={company.logo} alt="" className="w-6 h-6 rounded-full mr-3 flex-shrink-0" data-id="element-13683" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-13684">
                            <span className="text-xs font-medium text-gray-600" data-id="element-13685">
                              {company.name.charAt(0)}
                            </span>
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-13686">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-13687">
                            {company.name}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0" data-id="element-13688">
                        {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-13689" />}
                      </div>
                    </div>;
          })}
            </div>

            {/* Sub Merchants section */}
            <div data-id="element-13690">
              <div className="px-3 py-2 bg-gray-50" data-id="element-13691">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-13692">
                  Sub Merchants
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto" data-id="element-13693">
                {companies.filter(c => c.type === 'submerchant').map(company => {
              const isSelected = selectedCompany.id === company.id;
              return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-13694">
                        <div className="flex items-center flex-1 min-w-0" data-id="element-13695">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-13696">
                            <BuildingIcon size={12} className="text-gray-500" data-id="element-13697" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-13698">
                            <div className="text-sm text-gray-900 truncate" data-id="element-13699">
                              {company.name}
                            </div>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0" data-id="element-13700">
                          {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-13701" />}
                        </div>
                      </div>;
            })}
              </div>
            </div>
          </div>
        </>}
    </div>;
}